import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, Heart, MapPin, Timer, Trophy, Users, Leaf } from "lucide-react";
import { Seo } from "../components/Seo";
import { Countdown } from "../components/Countdown";
import { Reveal, MaskLine } from "../components/Reveal";
import { Kicker, CTAButton } from "../components/Bits";
import { EVENT, LINKS, CHARITY_URL, HIGHLIGHTS, IMAGES, SCHEDULE, AO_BADGE } from "../lib/site";
import { SponsorMarquee } from "../components/SponsorMarquee";

const iconFor = [Timer, Users, Heart, Trophy];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

  return (
    <>
      <Seo title="Home" path="/" />

      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[100svh] flex items-end overflow-hidden" data-testid="hero">
        <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 -z-10">
          <img src={IMAGES.hero} alt="Runner on an autumn trail" className="h-full w-full object-cover object-[60%_center]" />
        </motion.div>
        <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 -z-10 bg-gradient-to-t from-ink via-ink/50 to-ink/20" />

        <div className="relative mx-auto max-w-7xl w-full px-5 sm:px-8 pb-16 sm:pb-24 pt-28">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-2 text-cream/90"
          >
            <Leaf size={18} className="text-ochre" />
            <span className="font-accent text-2xl text-ochre">{EVENT.dateLabel}</span>
          </motion.div>

          <h1 className="mt-3 font-display font-semibold text-white tracking-tight text-[3.25rem] leading-[0.95] sm:text-7xl lg:text-8xl">
            <MaskLine delay={0.1}><span className="text-ochre">Grimsby</span></MaskLine>
            <MaskLine delay={0.22}>On The Lake</MaskLine>
            <MaskLine delay={0.34}>
              <span className="text-ochre">Turkey Trot</span>
            </MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="mt-6 max-w-xl text-base sm:text-lg text-cream/85 leading-relaxed"
          >
            A family-friendly 5 km run/walk & Kids Dash on the shore of Lake Ontario in Grimsby,
            raising every dollar for {EVENT.charity}.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <CTAButton href={LINKS.register} data-testid="hero-register">
              Register Now <ArrowUpRight size={16} />
            </CTAButton>
            <CTAButton href={LINKS.donate} variant="outline" data-testid="hero-donate">
              <span className="text-cream">Donate</span>
            </CTAButton>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1 }}
            className="mt-10"
          >
            <Countdown target={EVENT.date} dark />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.15 }}
            className="mt-8 inline-flex items-center gap-3 rounded-full bg-white/10 backdrop-blur-md border border-white/20 pl-2 pr-5 py-2"
            data-testid="ao-badge-hero"
          >
            <img src={AO_BADGE} alt="Athletics Ontario Sanctioned Race 2026" className="w-9 h-9 shrink-0" />
            <span className="text-xs sm:text-sm font-medium text-cream/90 tracking-wide">
              Athletics Ontario Sanctioned Race · 2026
            </span>
          </motion.div>
        </div>
      </section>

      {/* SPONSOR STRIP — TOP */}
      <SponsorMarquee testid="sponsor-strip-top" />

      {/* MARQUEE */}
      <div className="relative z-10 bg-terracotta text-cream py-4 border-y border-white/10">
        <Marquee speed={40} gradient={false} className="marquee-mask">
          {Array.from({ length: 6 }).map((_, i) => (
            <span key={i} className="mx-8 font-display text-2xl sm:text-3xl font-medium inline-flex items-center gap-6">
              5 km Run / Walk <Leaf size={18} className="text-ochre" /> Kids 1 km Dash
              <Leaf size={18} className="text-ochre" /> Every Dollar Gives Back
              <Leaf size={18} className="text-ochre" />
            </span>
          ))}
        </Marquee>
      </div>

      {/* HIGHLIGHTS — TETRIS GRID */}
      <section className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32" data-testid="highlights">
        <div className="max-w-2xl">
          <Kicker>why we trot</Kicker>
          <h2 className="mt-2 font-display font-semibold tracking-tight text-4xl sm:text-5xl">
            A morning of movement, family & giving.
          </h2>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-6 lg:gap-8">
          {HIGHLIGHTS.map((h, i) => {
            const Icon = iconFor[i];
            const spans = [
              "md:col-span-5 lg:col-span-7",
              "md:col-span-3 lg:col-span-5",
              "md:col-span-8 lg:col-span-12",
            ];
            const themes = [
              { card: "bg-forest text-cream border-forest", icon: "bg-cream/15 text-ochre", desc: "text-cream/75" },
              { card: "bg-terracotta text-white border-terracotta", icon: "bg-white/20 text-white", desc: "text-white/85" },
              { card: "bg-ochre text-ink border-ochre", icon: "bg-ink/10 text-ink", desc: "text-ink/70" },
            ];
            const t = themes[i % themes.length];
            return (
              <Reveal key={h.title} delay={i * 0.08} className={spans[i]}>
                <a
                  href={LINKS.register}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`group block h-full rounded-3xl p-8 sm:p-10 border shadow-warm-lg transition-transform duration-300 hover:-translate-y-1 cursor-pointer ${t.card}`}
                  data-testid={`highlight-card-${i}`}
                >
                <div className="flex items-start justify-between gap-4">
                  <div className={`grid place-items-center h-12 w-12 rounded-2xl ${t.icon}`}>
                    <Icon size={22} />
                  </div>
                  {h.price && (
                    <span className="font-display text-3xl sm:text-4xl font-semibold leading-none" data-testid={`highlight-price-${i}`}>
                      {h.price}
                    </span>
                  )}
                </div>
                <h3 className="mt-6 font-display text-2xl sm:text-3xl font-semibold">{h.title}</h3>
                <p className={`mt-3 text-base leading-relaxed ${t.desc}`}>{h.desc}</p>
                </a>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* CHARITY SPOTLIGHT */}
      <section className="relative z-10 bg-cream-200 py-24 sm:py-32" data-testid="charity-spotlight">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid gap-12 lg:grid-cols-2 items-center">
          <Reveal>
            <a href={CHARITY_URL} target="_blank" rel="noopener noreferrer" className="relative block group" data-testid="charity-image-link">
              <img
                src={IMAGES.charity}
                alt="Autumn community path"
                className="rounded-3xl object-cover w-full h-[420px] shadow-warm-lg transition-transform duration-500 group-hover:scale-[1.02]"
              />
              <div className="absolute -bottom-6 -right-4 sm:right-6 bg-terracotta text-white rounded-2xl px-6 py-5 shadow-warm-lg">
                <p className="font-display text-4xl font-semibold leading-none">100%</p>
                <p className="text-xs uppercase tracking-widest mt-1 text-white/85">of proceeds donated</p>
              </div>
            </a>
          </Reveal>
          <Reveal delay={0.12}>
            <Kicker>the cause</Kicker>
            <h2 className="mt-2 font-display font-semibold tracking-tight text-4xl sm:text-5xl">
              Running for {EVENT.charity}.
            </h2>
            <p className="mt-5 text-base sm:text-lg text-ink-soft leading-relaxed">
              Inclusion West Niagara empowers people of all abilities to live, work and belong fully in
              our community. Your registration and donations fund inclusive programs, employment support
              and lifelong friendships right here in Niagara.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <CTAButton href={CHARITY_URL} variant="forest" data-testid="charity-visit">
                <Heart size={16} /> Support Inclusion West Niagara
              </CTAButton>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-ink/20 text-sm font-semibold hover:border-terracotta hover:text-terracotta transition-colors duration-200"
              >
                Our story <ArrowUpRight size={16} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SPONSOR STRIP — BOTTOM */}
      <SponsorMarquee testid="sponsor-strip-bottom" />

      {/* SCHEDULE PREVIEW */}
      <section className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32" data-testid="schedule-preview">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-xl">
            <Kicker>race day</Kicker>
            <h2 className="mt-2 font-display font-semibold tracking-tight text-4xl sm:text-5xl">
              Saturday morning, start to finish.
            </h2>
          </div>
          <Link to="/schedule" className="inline-flex items-center gap-2 text-sm font-semibold text-terracotta hover:gap-3 transition-all duration-200">
            Full schedule <ArrowUpRight size={16} />
          </Link>
        </div>

        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {SCHEDULE.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.08}>
              <div className="h-full rounded-2xl bg-white border border-black/5 p-6 shadow-warm">
                <p className="font-display text-3xl font-semibold text-terracotta">{s.time}</p>
                <h3 className="mt-3 font-semibold text-lg">{s.title}</h3>
                <p className="mt-2 text-sm text-ink-soft leading-relaxed">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* LOCATION CTA */}
      <section className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 pb-24 sm:pb-32">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl px-8 py-14 sm:px-16 sm:py-24 text-cream">
            <img src={IMAGES.grimsby} alt="Aerial sunset view of Grimsby on the lake" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-forest via-forest/90 to-forest/30" />
            <div className="relative max-w-xl">
              <p className="flex items-center gap-2 text-ochre">
                <MapPin size={18} /> <span className="font-accent text-2xl">Casablanca Beach</span>
              </p>
              <h2 className="mt-2 font-display font-semibold tracking-tight text-4xl sm:text-5xl">
                Meet us on the shoreline.
              </h2>
              <p className="mt-4 text-cream/80 leading-relaxed">
                {EVENT.location.name}, {EVENT.location.address}. Free parking at Church on Hunter, lake views and a whole lot of turkey spirit.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <CTAButton href={LINKS.register}>Register Now <ArrowUpRight size={16} /></CTAButton>
                <Link to="/race-map" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-cream/30 text-sm font-semibold hover:bg-cream/10 transition-colors duration-200">
                  View race map
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </>
  );
}
